import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CoreContext } from "../../context/global/CoreContext";
import { getPayoutTimesheetDashboard } from "../../services/api/timekeeping";
import { Spinner } from "../../components/ui";
import PayrollWeekSection from "../../features/timekeeping/payoutTimesheetDashboard/PayrollWeekSection";
import {
  getPayoutDashboardSummary,
  isNoHoursPayoutDashboardRow,
  weekHasShifts,
} from "../../features/timekeeping/payoutTimesheetDashboard/summaryUtils";
import {
  getDateInLocalTZFromDateStr,
  getMonthDateRange,
  getRecentMonthOptions,
} from "../../utils/datetime";

const PAYOUT_DASHBOARD_REFRESH_CHANNEL = "payout-timesheet-dashboard-refresh";
const PAYOUT_DASHBOARD_REFRESH_EVENT = "payout-timesheet-saved";

/**
 * PayoutTimesheetDashboard renders pending payout rows and historical weekly payroll summaries.
 *
 * @returns {JSX.Element} Payout timesheet dashboard page.
 */
const PayoutTimesheetDashboard = () => {
  const { user, appName } = useContext(CoreContext);
  // Selected month follows the newest completed shift until the user picks one.
  const selectedMonthRef = useRef("");
  const [latestShiftWeekEnding, setLatestShiftWeekEnding] = useState("");
  const [selectedMonthValue, setSelectedMonthValue] = useState("");
  const monthOptions = useMemo(() => {
    const anchor = latestShiftWeekEnding
      ? getDateInLocalTZFromDateStr(latestShiftWeekEnding)
      : undefined;
    return getRecentMonthOptions(12, anchor);
  }, [latestShiftWeekEnding]);
  const [dashboardData, setDashboardData] = useState({
    weeks: [],
    currentWeekEnding: null,
  });
  const [expandedWeeks, setExpandedWeeks] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeNoShiftTimesheets, setIncludeNoShiftTimesheets] = useState(false);
  const visibleWeeks = useMemo(() => {
    return dashboardData.weeks.filter(weekHasShifts).map((week) => {
      const timesheets = includeNoShiftTimesheets
        ? (week.timesheets || [])
        : (week.timesheets || []).filter(
          (timesheet) => Number(timesheet.shiftCount) !== 0
        );
      const summaryTimesheets = timesheets.filter(
        (timesheet) => !isNoHoursPayoutDashboardRow(
          timesheet,
          week.statuses?.iifExported
        )
      );

      return {
        ...week,
        timesheets,
        summary: {
          ...week.summary,
          ...getPayoutDashboardSummary(summaryTimesheets),
        },
      };
    });
  }, [dashboardData.weeks, includeNoShiftTimesheets]);

  const fetchDashboard = useCallback(
    async ({ resetExpandedWeeks = false, silent = false, monthOverride } = {}) => {
      const month = monthOverride ?? selectedMonthRef.current;

      if (!silent) {
        setIsLoading(true);
      }
      setError("");
      try {
        // The first load omits the month so the API uses the newest shift week.
        const params = {
          include_empty: includeNoShiftTimesheets,
        };
        if (month) {
          Object.assign(params, getMonthDateRange(month));
        }
        const response = await getPayoutTimesheetDashboard(params);
        const weeks = response.weeks || [];
        const anchorWeekEnding = response.latestShiftWeekEnding || "";
        if (anchorWeekEnding) {
          setLatestShiftWeekEnding(anchorWeekEnding);
        }
        if (!month && anchorWeekEnding) {
          const anchorMonth = anchorWeekEnding.slice(0, 7);
          selectedMonthRef.current = anchorMonth;
          setSelectedMonthValue(anchorMonth);
        }
        setDashboardData({
          weeks,
          currentWeekEnding: response.currentWeekEnding || null,
        });
        if (resetExpandedWeeks) {
          setExpandedWeeks(
            new Set(
              weeks
                .filter((week) => !week.statuses?.iifExported)
                .map((week) => week.weekEnding)
            )
          );
        }
      } catch (error) {
        console.error("Error fetching payout dashboard:", error);
        setError("Unable to load payout dashboard data.");
      } finally {
        setIsLoading(false);
      }
    },
    [includeNoShiftTimesheets]
  );

  useEffect(() => {
    fetchDashboard({ resetExpandedWeeks: true });
  }, [fetchDashboard]);

  useEffect(() => {
    const supportsBroadcastChannel = typeof window !== "undefined" &&
      "BroadcastChannel" in window;
    /**
     * Silently reloads dashboard rows after another tab saves a timesheet.
     *
     * @returns {void}
     */
    const refreshDashboard = () => fetchDashboard({ silent: true });

    if (supportsBroadcastChannel) {
      const channel = new BroadcastChannel(PAYOUT_DASHBOARD_REFRESH_CHANNEL);
      channel.onmessage = (event) => {
        if (event.data?.type === PAYOUT_DASHBOARD_REFRESH_EVENT) {
          refreshDashboard();
        }
      };
      return () => channel.close();
    }

    /**
     * Fallback cross-tab refresh via `storage` when BroadcastChannel is unavailable.
     *
     * @param {StorageEvent} event Window storage event.
     * @returns {void}
     */
    const handleStorage = (event) => {
      if (event.key === PAYOUT_DASHBOARD_REFRESH_CHANNEL && event.newValue) {
        refreshDashboard();
      }
    };

    window.addEventListener("storage", handleStorage);
    return () => window.removeEventListener("storage", handleStorage);
  }, [fetchDashboard]);

  /**
   * Opens a payout timesheet in a new tab.
   *
   * @param {number} workerID Worker user id.
   * @param {string} weekEnding Week-ending date key.
   * @returns {void}
   */
  const handleTimesheetSelect = (workerID, weekEnding) => {
    // Open the payout timesheet in a new tab.
    window.open(
      `/app/timekeeping/payout-timesheet/${workerID}/${weekEnding}`,
      "_blank"
    );
  };

  /**
   * Loads the payroll weeks whose Saturday falls in the chosen month.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event Month select change.
   * @returns {void}
   */
  const handleMonthChange = (event) => {
    const month = event.target.value;
    selectedMonthRef.current = month;
    setSelectedMonthValue(month);
    fetchDashboard({ resetExpandedWeeks: true, monthOverride: month });
  };

  /**
   * Expands or collapses a week group on the dashboard.
   *
   * @param {string} weekEnding Week-ending date key.
   * @returns {void}
   */
  const handleToggleWeek = (weekEnding) => {
    setExpandedWeeks((current) => {
      const next = new Set(current);
      if (next.has(weekEnding)) {
        next.delete(weekEnding);
      } else {
        next.add(weekEnding);
      }
      return next;
    });
  };

  React.useEffect(() => {
    document.title = `Timesheet Payout Review`;
    if (appName) {
      appName.current = "Timesheet Payout Review";
    }
  }, [user, appName]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-custom-rsbg">
        <Spinner size="h-12 w-12" className="text-green-900" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-custom-rsbg font-inter text-gray-900">
      <div className="relative isolate overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-rsbg" />

        <div className="mx-auto flex max-w-7xl flex-col gap-2 px-3 py-4 sm:px-5 lg:px-6">
          <header className="flex items-center justify-between gap-3 rounded-lg bg-white px-4 py-3 shadow-md shadow-gray-300">
            <div className="min-w-0 flex-1">
              <h1 className="truncate text-xl font-bold tracking-tight text-gray-950">
                Timesheet Payout Review
              </h1>
            </div>

            <div className="flex flex-shrink-0 flex-col items-end gap-2">
              <select
                className="min-h-[28px] rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm font-semibold text-gray-950 outline-none transition focus:border-secondary-400 focus:bg-white focus:ring-4 focus:ring-secondary-100"
                value={selectedMonthValue}
                onChange={handleMonthChange}
              >
                {monthOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <label className="flex items-center gap-2 text-xs text-gray-600">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5 rounded border-gray-300 text-green-900 focus:ring-green-900"
                  checked={includeNoShiftTimesheets}
                  onChange={(event) => setIncludeNoShiftTimesheets(event.target.checked)}
                />
                Include Timesheets with no Shifts
              </label>
            </div>
          </header>

          {error ? (
            <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
              {error}
            </div>
          ) : null}

          <section className="space-y-2">
            {visibleWeeks.length === 0 ? (
              <div className="rounded-lg border border-white/70 bg-white px-4 py-6 text-center text-sm text-gray-500 shadow shadow-primary-900/10">
                No payout timesheets found for this month.
              </div>
            ) : (
              <div className="space-y-2">
                {visibleWeeks.map((week) => (
                  <PayrollWeekSection
                    key={week.weekEnding}
                    week={week}
                    isExpanded={expandedWeeks.has(week.weekEnding)}
                    onToggle={handleToggleWeek}
                    onSelect={handleTimesheetSelect}
                  />
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </main>
  );
};

export default PayoutTimesheetDashboard;
